import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import { fetchCurrentUser, login as loginRequest, logout as logoutRequest } from '@/services/auth';
import type { LoginCredentials } from '@/services/auth';
import type { User } from '@/types';

export const useAuthStore = defineStore('auth', () => {
    const user = ref<User | null>(null);

    const isAuthenticated = computed(() => user.value !== null);

    function hasRole(role: string): boolean {
        return user.value?.roles?.includes(role) ?? false;
    }

    function hasPermission(permission: string): boolean {
        return user.value?.permissions?.includes(permission) ?? false;
    }

    /** Users and its reports need both, as the API enforces (Nova's UserPolicy). */
    const canSeeUsers = computed(() => hasRole('Super Admin') && hasPermission('View Users'));

    /** Changing labels (and, later, users in bulk) also needs Edit Users, as in Nova. */
    const canEditUsers = computed(() => canSeeUsers.value && hasPermission('Edit Users'));

    async function fetchUser(): Promise<User | null> {
        try {
            user.value = await fetchCurrentUser();
            return user.value;
        } catch {
            user.value = null;
            return null;
        }
    }

    async function login(credentials: LoginCredentials): Promise<User> {
        user.value = await loginRequest(credentials);
        return user.value;
    }

    async function logout(): Promise<void> {
        // Best-effort: if the API call fails (network drop, expired session),
        // we still want to clear local auth state and return cleanly so the
        // UI can navigate back to the login page.
        try {
            await logoutRequest();
        } catch {
            // intentionally swallowed
        } finally {
            user.value = null;
        }
    }

    /** Test helper. Avoids tests needing to monkey-patch the ref directly. */
    function $reset(): void {
        user.value = null;
    }

    return {
        user,
        isAuthenticated,
        hasRole,
        hasPermission,
        canSeeUsers,
        canEditUsers,
        fetchUser,
        login,
        logout,
        $reset,
    };
});
